import React from "react";
import { useTranslation } from "react-i18next";
import TimeService from "src/services/timeService/timeService";
import { useDispatch, useSelector } from "src/store";
import { Rollout } from "src/ui/containers/dashboard/rollouts/types";

import { closeDetails, selectDetailsRollout } from "@dashboard/devices/store";
import { useGetRolloutByIdQuery } from "@dashboard/rollouts/api";
import { Box, Chip, ChipProps, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { PropertyKey, PropertyRow, PropertyValue } from "@sidePanel/shared/Property";

import { ClosePanelButton } from "../shared/ClosePanelButton";
import { PaneElement } from "../shared/PaneElement";
import { useStyle } from "../shared/style";

const statusChipColor: Record<Rollout["status"], ChipProps["color"]> = {
    active: "info",
    completed: "success",
    cancelled: "default",
};

const statusLabelKey = {
    active: "common:status.active",
    completed: "common:status.completed",
    cancelled: "common:status.cancelled",
} as const satisfies Record<Rollout["status"], string>;

export const RolloutSidePanel = () => {
    const { t } = useTranslation(["sidepanels", "common"]);
    const dispatch = useDispatch();
    const { classes } = useStyle();

    const details = useSelector(selectDetailsRollout);
    const { data, isFetching } = useGetRolloutByIdQuery(details ? { id: details.selectedId } : skipToken);
    const rollout = data?.model ?? null;

    if (!details) {
        return null;
    }

    const schedule =
        rollout?.installWindowStart && rollout?.installWindowEnd
            ? t("sidepanels:rolloutPanel.confirm.scheduleTimeWindow", {
                  start: TimeService.getLocalDate(rollout.installWindowStart, "ISO", "dateTime"),
                  end: TimeService.getLocalDate(rollout.installWindowEnd, "ISO", "dateTime"),
              })
            : t("sidepanels:rolloutPanel.confirm.scheduleWhenFound");

    return (
        <React.Fragment>
            <PaneElement.HeaderBox>
                <Typography variant="h6" className={classes.panelTitle}>
                    {t("sidepanels:rolloutDetailsPanel.title")}
                </Typography>
                <Box className={classes.buttonBox}>
                    <ClosePanelButton onClick={() => dispatch(closeDetails())} />
                </Box>
            </PaneElement.HeaderBox>

            <PaneElement.Container>
                {isFetching && !rollout ? (
                    <PaneElement.Loader />
                ) : !rollout ? (
                    <PaneElement.NoData message={t("common:error.rollouts.failedToFetchRollout")} />
                ) : (
                    <PaneElement.Section withoutBorder>
                        <PropertyRow>
                            <PropertyKey>{t("common:label.version")}</PropertyKey>
                            <PropertyValue>{rollout.version}</PropertyValue>
                        </PropertyRow>
                        <PropertyRow>
                            <PropertyKey>{t("common:label.codename")}</PropertyKey>
                            <PropertyValue>{rollout.codename}</PropertyValue>
                        </PropertyRow>
                        <PropertyRow>
                            <PropertyKey>{t("common:label.status")}</PropertyKey>
                            <PropertyValue>
                                <Chip
                                    label={t(statusLabelKey[rollout.status])}
                                    size="small"
                                    color={statusChipColor[rollout.status]}
                                />
                            </PropertyValue>
                        </PropertyRow>
                        <PropertyRow>
                            <PropertyKey>{t("common:label.progress")}</PropertyKey>
                            <PropertyValue>{rollout.progress}%</PropertyValue>
                        </PropertyRow>
                        <PropertyRow>
                            <PropertyKey>{t("common:label.created")}</PropertyKey>
                            <PropertyValue>
                                {rollout.created ? TimeService.getLocalDate(rollout.created, "ISO", "dateTime") : "-"}
                            </PropertyValue>
                        </PropertyRow>
                        <PropertyRow>
                            <PropertyKey>{t("sidepanels:rolloutDetailsPanel.properties.devices")}</PropertyKey>
                            <PropertyValue>
                                {t("sidepanels:rolloutPanel.confirm.devicesCount", { count: rollout.deviceIds.length })}
                            </PropertyValue>
                        </PropertyRow>
                        <PropertyRow>
                            <PropertyKey>{t("sidepanels:rolloutDetailsPanel.properties.schedule")}</PropertyKey>
                            <PropertyValue>{schedule}</PropertyValue>
                        </PropertyRow>
                        {rollout.cancelled ? (
                            <PropertyRow>
                                <PropertyKey>{t("common:status.cancelled")}</PropertyKey>
                                <PropertyValue>
                                    {t("sidepanels:rolloutDetailsPanel.cancelledOn", {
                                        date: TimeService.getLocalDate(rollout.cancelled, "ISO", "dateTime"),
                                    })}
                                </PropertyValue>
                            </PropertyRow>
                        ) : null}
                    </PaneElement.Section>
                )}
            </PaneElement.Container>
        </React.Fragment>
    );
};
